// src/pages/DJQrPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import QRCode from "react-qr-code";
import api from "../api";
import "./DJQrPage.css";
import { FaLink, FaDownload, FaImage, FaTimes } from "react-icons/fa";
import { SERVER_URL } from "../config";

const APP_BASE_URL = "https://requesta.app";

const svgToDataUrl = (svgEl: SVGSVGElement): Promise<string> =>
  new Promise((resolve) => {
    const serialized = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([serialized], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const size = 300;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = url;
  });

interface Profile {
  name: string;
  slug: string;
  avatarUrl?: string;
}

const loadImageAsDataUrl = (url: string): Promise<string | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width;
      c.height = img.height;
      const ctx = c.getContext("2d");
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0);
      resolve(c.toDataURL("image/jpeg"));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const DJQrPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrLogoDataUrl, setQrLogoDataUrl] = useState<string | null>(null);
  const svgWrapperRef = useRef<HTMLDivElement | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!slug) return;

    api
      .get<Profile>(`/users/${slug}`)
      .then(async (res) => {
        setProfile(res.data);
        // Pre-load avatar as default QR center logo
        if (res.data.avatarUrl) {
          const absUrl = res.data.avatarUrl.startsWith("http")
            ? res.data.avatarUrl
            : `${SERVER_URL}${res.data.avatarUrl}`;
          const dataUrl = await loadImageAsDataUrl(absUrl);
          if (dataUrl) setQrLogoDataUrl(dataUrl);
        }
      })
      .catch((err) => {
        console.error("Error fetching DJ profile:", err);
      });
  }, [slug]);

  // Convert rendered SVG QR → PNG data URL once profile is loaded
  useEffect(() => {
    if (!profile) return;
    const timer = setTimeout(async () => {
      const svgEl = svgWrapperRef.current?.querySelector("svg");
      if (!svgEl) return;
      const dataUrl = await svgToDataUrl(svgEl as SVGSVGElement);
      setQrDataUrl(dataUrl);
    }, 100);
    return () => clearTimeout(timer);
  }, [profile]);

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (ev.target?.result) setQrLogoDataUrl(ev.target.result as string);
    };
    reader.readAsDataURL(file);
    // Reset so same file can be re-selected
    e.target.value = "";
  };

  const buildFinalCanvas = (): Promise<HTMLCanvasElement | null> =>
    new Promise((resolve) => {
      if (!profile || !qrDataUrl) { resolve(null); return; }

      const qrImg = new Image();
      qrImg.src = qrDataUrl;

      qrImg.onload = () => {
        const padding = 40;
        const qrSize = qrImg.width;
        const canvasWidth = qrSize + padding * 2;
        const canvasHeight = qrSize + padding * 3;

        const canvas = document.createElement("canvas");
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(null); return; }

        // White background
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // Top text
        ctx.fillStyle = "#000000";
        ctx.font = "bold 24px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Request a Song", canvasWidth / 2, padding);

        // Draw QR
        ctx.drawImage(qrImg, padding, padding * 1.5);

        // Bottom text
        ctx.font = "18px sans-serif";
        ctx.fillText(profile.name, canvasWidth / 2, canvasHeight - padding / 2);

        if (!qrLogoDataUrl) {
          resolve(canvas);
          return;
        }

        // Overlay custom logo in center
        const logoImg = new Image();
        logoImg.onload = () => {
          const logoSize = qrSize * 0.22;
          const logoX = padding + (qrSize - logoSize) / 2;
          const logoY = padding * 1.5 + (qrSize - logoSize) / 2;

          // White padding behind logo for readability
          const bgPad = 6;
          const bgRadius = logoSize * 0.18 + bgPad;
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, bgRadius, 0, Math.PI * 2);
          ctx.fill();

          // Clip logo to circle
          ctx.save();
          ctx.beginPath();
          ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
          ctx.restore();

          resolve(canvas);
        };
        logoImg.onerror = () => resolve(canvas);
        logoImg.src = qrLogoDataUrl;
      };
    });

  const handleSave = async () => {
    const canvas = await buildFinalCanvas();
    if (!canvas) return;

    canvas.toBlob(async (blob) => {
      if (!blob) return;

      const filename = `qr-${profile!.slug}.png`;
      const file = new File([blob], filename, { type: "image/png" });

      // Try native share sheet — on iOS this shows "Save Image" → Photos
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "My QR Code" });
          return;
        } catch (err: any) {
          if (err.name === "AbortError") return; // user cancelled
          console.error("Share failed:", err);
        }
      }

      // Fallback: standard file download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, "image/png");
  };

  if (!profile) {
    return <p style={{ textAlign: "center", marginTop: "2rem" }}>Loading...</p>;
  }

  const djUrl = `${APP_BASE_URL}/dj/${slug}/request`;

  return (
    <div className="qr-container">
      {/* Hidden SVG used only to generate the PNG data URL */}
      <div ref={svgWrapperRef} style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}>
        <QRCode value={djUrl} size={300} />
      </div>

      <h2 className="qr-title">My QR Code</h2>
      <h3 className="qr-h3">Request a Song</h3>

      <div className="qr-preview-wrapper">
        {qrDataUrl ? (
          <img src={qrDataUrl} alt="DJ QR Code" className="qr-image" />
        ) : (
          <div style={{ width: 200, height: 200, background: "#1f2937", borderRadius: 8, margin: "0 auto" }} />
        )}
        {qrLogoDataUrl && (
          <img src={qrLogoDataUrl} alt="QR center logo" className="qr-center-logo" />
        )}
      </div>

      <h2>{profile.name}</h2>

      <div className="qr-buttons">
        <div className="link-block-wrap">
          <Link
            to={`/dj/${slug}/request`}
            className="qr-btn"
            title="View my public page"
          >
            <FaLink />
          </Link>
          <p className="link-down-text">My Link</p>
        </div>

        <div className="donwload-block">
          <button
            onClick={handleSave}
            className="qr-btn"
            title="Save to Camera Roll"
          >
            <FaDownload />
          </button>
          <p className="down-down-text">Save to Camera Roll</p>
        </div>

        <div className="donwload-block">
          <button
            onClick={() => logoInputRef.current?.click()}
            className="qr-btn"
            title="Upload custom center image"
          >
            <FaImage />
          </button>
          <p className="down-down-text">Custom Logo</p>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleLogoFileChange}
          />
        </div>
      </div>

      {qrLogoDataUrl && (
        <button className="qr-remove-logo-btn" onClick={() => setQrLogoDataUrl(null)}>
          <FaTimes size={12} style={{ marginRight: 4 }} />
          Remove logo
        </button>
      )}
    </div>
  );
};

export default DJQrPage;
